import { Box, Skeleton, SkeletonBox } from "@hightouch/htui";
import {
  type EmailStyleConfiguration,
  wrapComponentForStandaloneRender,
} from "@hightouch/core/content/component-normalization/canonical-style";
import { injectBlockScriptsMeta } from "src/components/preview-canvas/email-preview-security";
import { EMAIL_PREVIEW_DESKTOP_WIDTH } from "src/components/email-preview";

// Lay the email out at a desktop width (then scale down) so it renders its
// desktop layout rather than tripping its mobile breakpoint — see
// EMAIL_PREVIEW_DESKTOP_WIDTH.
const IFRAME_WIDTH = EMAIL_PREVIEW_DESKTOP_WIDTH;

const sizeConfig = {
  sm: {
    width: 40,
    height: 30,
    borderRadius: "sm",
  },
  md: {
    width: 60,
    height: 45,
    borderRadius: "md",
  },
} as const;

type EmailThumbnailProps = {
  html: string | null | undefined;
  name: string;
  size?: keyof typeof sizeConfig;
  isLoading?: boolean;
  /**
   * When true, `html` is a headless component fragment: wrap it with the
   * canonical responsive block + content-width body rule so the thumbnail sits
   * in its centered column instead of stretching full-width.
   */
  isComponent?: boolean;
  /** Brand email style config for the component's content width. */
  emailStyleConfig?: EmailStyleConfiguration;
};

export const EmailThumbnail = ({
  html,
  name,
  size = "md",
  isLoading = false,
  isComponent = false,
  emailStyleConfig,
}: EmailThumbnailProps) => {
  const config = sizeConfig[size];
  const scale = config.width / IFRAME_WIDTH;

  if (isLoading) {
    return (
      <Skeleton isLoading>
        <SkeletonBox
          width={`${config.width}px`}
          height={`${config.height}px`}
          borderRadius={config.borderRadius}
        />
      </Skeleton>
    );
  }

  if (!html) {
    return (
      <Box
        width={`${config.width}px`}
        height={`${config.height}px`}
        bg="base.lightBackground"
        borderRadius={config.borderRadius}
        border="1px solid"
        borderColor="base.border"
      />
    );
  }

  const srcDoc = isComponent
    ? wrapComponentForStandaloneRender(html, emailStyleConfig)
    : html;

  return (
    <Box
      position="relative"
      width={`${config.width}px`}
      height={`${config.height}px`}
      overflow="hidden"
      borderRadius={config.borderRadius}
      border="1px solid"
      borderColor="base.border"
    >
      <Box
        key={srcDoc}
        as="iframe"
        title={`Thumbnail of ${name}`}
        srcDoc={injectBlockScriptsMeta(srcDoc)}
        sandbox=""
        pointerEvents="none"
        position="absolute"
        top="0"
        left="0"
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
          width: `${IFRAME_WIDTH}px`,
          height: `${config.height / scale}px`,
        }}
      />
    </Box>
  );
};
